import { Component, Inject, Input, ViewChild } from '@angular/core';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { ContactService } from '../../services/contact.service';
import { CRMService } from '../../services/crm.service';
import { EmployeeService } from '../../services/employees.service';
import { OrderService } from '../../services/order.service';
import { Customer, Employee, Opportunity, Product, SalesCycle, Contact, OpportunitySalesCycleMap, SearchCustomer } from '../../models/models';

@Component({
    selector: 'edit-opportunity',
    templateUrl: './edit-opportunity.component.html',
    standalone: false
})
export class EditOpportunityDialog {
    @Input() opportunityId: number = 0;

    @ViewChild('customerDropdown', { static: false }) customerDropdown!: MatAutocomplete;

    public customer: Customer = {} as Customer;
    public opportunitySalesCycleId: number = 0;
    public employee: Employee = {} as Employee;
    public opportunity: Opportunity = {} as Opportunity;
    public opportunitySalesCycle: OpportunitySalesCycleMap = {} as OpportunitySalesCycleMap;
    public customers: SearchCustomer[] = [];
    public filteredCustomers: Observable<SearchCustomer[]> = new Observable<SearchCustomer[]>();
    public products: Product[] = [];
    public salesCycles: SalesCycle[] = [];
    public contacts: Contact[] = [];
    public employees: Employee[] = [];
    public submitted: boolean = false;
    public savingOpportunity: boolean = false;
    public loadingCustomers: boolean = false;

    public addNewContact: boolean = false;
    public contactName: string = '';
    public contactPhone: string = '';
    public contactEmail: string = '';
    public contactTitle: string = '';
    public contactSaveMessage: string = '';
    public phoneMask: string = "(999) 000-0000";

    public opportunityGroup: UntypedFormGroup = new UntypedFormGroup({
        customerCtrl: new UntypedFormControl(''),
        ownerCtrl: new UntypedFormControl(-1),
        opportunityName: new UntypedFormControl(''),
        productCtrl: new UntypedFormControl(''),
        closeDate: new UntypedFormControl(''),
        salesCycleCtrl: new UntypedFormControl(''),
        contactCtrl: new UntypedFormControl(''),
        amountCtrl: new UntypedFormControl('')
    });

    constructor(public dialogRef: MatDialogRef<EditOpportunityDialog>, @Inject(MAT_DIALOG_DATA) public data: any,
        public employeeService: EmployeeService, public crmService: CRMService, private formBuilder: UntypedFormBuilder,
        private orderService: OrderService, private contactService: ContactService) {
        this.opportunityGroup = formBuilder.group({
            customerCtrl: ['', Validators.required],
            ownerCtrl: [-1, Validators.required],
            opportunityName: ['', Validators.required],
            productCtrl: ['', Validators.required],
            closeDate: ['', Validators.required],
            salesCycleCtrl: ['', Validators.required],
            contactCtrl: [''],
            amountCtrl: ['', Validators.required]
        });

        this.customer = data.customer;
        this.opportunityId = data.opportunityId;
        this.opportunitySalesCycle = data.opportunitySalesCycle;
        this.opportunitySalesCycleId = data.opportunitySalesCycle.opportunitySalesCycleMapId;

        if (this.customer == null || this.customer.customerID < 1) {
            this.getCustomers();
        } else {
            this.opportunityGroup.controls['customerCtrl'].setValue(this.customer.customerCode);
        }

        this.getEmployees();
        this.getProducts();
        this.getSalesCycles();
        this.getContacts();

        this.getOpportunity();
        this.getSalesCycleMap();

        this.filteredCustomers = this.opportunityGroup.controls['customerCtrl'].valueChanges
            .pipe(
                startWith(''),
                map(c => c ? this.filterCustomers(c) : new Array())
            );
    }

    getCustomers() {
        this.loadingCustomers = true;

        this.crmService.getSearchCustomer().subscribe(result => {
            this.loadingCustomers = false;
            this.customers = result.resultCollection;

            if (this.customer != null && this.customer.customerID > 0) {
                var customer = this.customers.filter(c => c.customerID == this.customer.customerID);
                this.opportunityGroup.controls['customerCtrl'].setValue(customer != null && customer.length > 0 ? customer[0] : '');
            }
        });
    }

    getEmployees() {
        this.employeeService.getEmployeesByGroupName('crm user').subscribe(result => {
            this.employees = result;
        });
    }

    getProducts() {
        this.orderService.getProducts().subscribe(result => {
            this.products = result;
        });
    }

    getSalesCycles() {
        this.crmService.getSalesCycles().subscribe(result => {
            this.salesCycles = result;
            this.setOpportunityValues();
        });
    }

    getContacts() {
        this.contacts = [];
        if ((this.customer == null || this.customer.customerID < 1) && this.opportunityGroup.controls['customerCtrl'].value == '') return;

        var customerId = (this.customer == null || this.customer.customerID < 1)
            ? (this.opportunityGroup.controls['customerCtrl'].value as SearchCustomer).customerID
            : this.customer.customerID;

        this.contactService.getContactsByTableIdType(customerId, 'customer').subscribe(result => {
            this.contacts = result;
            this.setOpportunityValues();
        });
    }

    getOpportunity() {
        if (this.opportunityId < 1) return;

        this.crmService.getOpportunityById(this.opportunityId).subscribe(result => {
            this.opportunity = result;
            this.setOpportunityValues();
        });
    }

    getSalesCycleMap() {
        if (this.opportunitySalesCycleId < 1) return;

        this.crmService.getOpportunitySalesCylceMapById(this.opportunitySalesCycleId).subscribe(result => {
            this.opportunitySalesCycle = result;
            this.setOpportunityValues();
        });
    }

    setOpportunityValues() {
        if (this.opportunityId < 1 || this.opportunity == null || this.opportunity.opportunityID == null || this.opportunity.opportunityID < 1) {
            this.opportunityGroup.controls['ownerCtrl'].setValue(parseInt(localStorage.getItem('EmployeeId')!));
            this.opportunityGroup.controls['salesCycleCtrl'].setValue(12);
            return;
        }

        var product = this.products.filter(p => p.productId == this.opportunity.productId);

        this.opportunityGroup.controls['ownerCtrl'].setValue(this.opportunity.ownerEmployeeId);
        this.opportunityGroup.controls['opportunityName'].setValue(this.opportunity.notes);
        this.opportunityGroup.controls['productCtrl'].setValue(this.opportunity.productId);
        this.opportunityGroup.controls['salesCycleCtrl'].setValue(this.opportunitySalesCycle.salesCycleId);
        this.opportunityGroup.controls['contactCtrl'].setValue(this.opportunity.contactId);
        this.opportunityGroup.controls['amountCtrl'].setValue(this.opportunity.estimatedRevenue);
        this.opportunityGroup.controls['closeDate'].setValue(new Date(this.opportunity.expectedCloseDate));
    }

    saveOpportunity() {
        this.submitted = true;

        if (!this.opportunityGroup.valid) return;

        this.savingOpportunity = true;
        var customerId = (this.customer == null || this.customer.customerID < 1)
            ? (this.opportunityGroup.controls['customerCtrl'].value as SearchCustomer).customerID
            : this.customer.customerID;
        //var dateFormat = "dd/MMM/yy";
        //var txtDate = this.intlService.formatDate(brokenDate, dateFormat);
        //var normalizedDate = this.intlService.parseDate(this.opportunityGroup.controls['closeDate'].value, dateFormat);

        var opp = {
            opportunityID: this.opportunityId,
            customerId: customerId,
            productId: this.opportunityGroup.controls['productCtrl'].value,
            ownerEmployeeId: this.opportunityGroup.controls['ownerCtrl'].value,
            estimatedRevenue: this.opportunityGroup.controls['amountCtrl'].value,
            contactId: this.opportunityGroup.controls['contactCtrl'].value,
            notes: this.opportunityGroup.controls['opportunityName'].value,
            expectedCloseDate: this.opportunityGroup.controls['closeDate'].value,
            createDate: new Date()
        } as Opportunity;

        if (opp.expectedCloseDate != null) {
            opp.expectedCloseDate = new Date(opp.expectedCloseDate);
        }

        this.crmService.saveOpportunity(opp).subscribe(result => {
            opp = result;

            var oppCycle = {
                opportunitySalesCycleMapId: this.opportunityId > 0 ? this.opportunitySalesCycle.opportunitySalesCycleMapId : 0,
                opportunity: opp,
                opportunityId: this.opportunityId,
                createdDate: new Date(),
                employeeId: parseInt(localStorage.getItem('EmployeeId')!),
                salesCycleId: this.opportunityGroup.controls['salesCycleCtrl'].value
            } as OpportunitySalesCycleMap;

            this.crmService.saveOpportunitySalesCycle(oppCycle).subscribe(result2 => {
                this.savingOpportunity = false;
                this.dialogRef.close();
            });
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    public saveContact() {
        this.contactSaveMessage = '';
        this.savingOpportunity = true;

        var newContact = {
            contactID: 0,
            contactType: 'Customer',
            tableID: this.customer.customerID,
            firstName: this.contactName.split(' ')[0],
            lastName: this.contactName.replace(this.contactName.split(' ')[0], ''),
            phone: this.contactPhone,
            email: this.contactEmail,
            title: this.contactTitle
        } as Contact;

        this.contactService.saveContact(newContact).subscribe(result => {
            this.savingOpportunity = false;

            if (result != null && result.contactID != null && result.contactID > 0) {
                this.getContacts();
                this.opportunityGroup.controls['contactCtrl'].setValue(result.contactID);
                this.addNewContact = false;
            } else {
                this.contactSaveMessage = 'Failed to save contact';
            }
        });
    }

    public chooseFirstCustomer() {
        if (this.customerDropdown.options.length < 1) return;

        this.opportunityGroup.controls['customerCtrl'].setValue(this.customerDropdown.options.first.value);
        this.customerChanged();
    }

    public customerChanged() {
        this.getContacts();
    }

    filterCustomers(customer: string) {
        if (typeof customer !== 'string') return new Array();
        const filterValue = customer.toLowerCase();

        return this.customers.filter(c => c.customerCode.toLowerCase().startsWith(filterValue));
    }

    displayCustomer(customer?: SearchCustomer): string | undefined {
        return customer ? customer.customerCode + ' - ' + customer.customerName : undefined;
    }

    //private alertUserForEstimatedRevenue() {
    //    if (this.opportunitySalesCycle != null && this.opportunitySalesCycle.opportunity != null && this.opportunitySalesCycle.salesCycle != null
    //        && (this.opportunitySalesCycle.opportunity.estimatedRevenue == null || this.opportunitySalesCycle.opportunity.estimatedRevenue < 1)
    //        && (this.opportunitySalesCycle.salesCycle.description == 'Won'
    //        || this.opportunitySalesCycle.salesCycle.description == 'Finalist(50-75% win)'
    //        || this.opportunitySalesCycle.salesCycle.description == 'Pre-Award (90%)')) {
    //        alert('Please enter an estimated revenue for the opportunity below.');
    //    }
    //}
}
